import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Lock, X } from 'lucide-react';
import { getPlan } from '../../data/pricingPlans';
import type { CheckoutPlanId } from '../../data/pricingPlans';
interface CheckoutAuthPromptProps {
  /** The plan the visitor selected, or null when the prompt is closed. */
  plan: CheckoutPlanId | null;
  onClose: () => void;
}
/**
 * Shown when an unauthenticated visitor starts checkout. The selected plan
 * is carried into the auth links so checkout resumes afterwards.
 */
export function CheckoutAuthPrompt({
  plan,
  onClose
}: CheckoutAuthPromptProps) {
  const isOpen = plan !== null;
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);
  const planName = plan ? getPlan(plan)?.name : null;
  const planPrice = plan ? getPlan(plan)?.price : null;
  return <AnimatePresence data-id="element-1612" data-mp="21kwp8ybkwdgi">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6" data-id="element-1613" data-mp="21hsf7kbfmnhx">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-1614" data-mp="2bib3cra14q5n" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="checkout-auth-title" initial={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 16
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative z-10 w-full max-w-md rounded-t-2xl border border-gray-800 bg-[#050505] p-6 sm:rounded-2xl sm:p-8" data-id="element-1615" data-mp="2u80v15ihwvtc">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 transition-colors duration-150 ease-out hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-white" data-id="element-1616" data-mp="2akksf3xwksn6">
              <X size={18} data-id="element-1617" data-mp="l90sencxlcqw" />
            </button>

            <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#FF0000]/30 bg-[#FF0000]/10" data-id="element-1618" data-mp="p7rgk4zf5k1v">
              <Lock className="h-5 w-5 text-[#FF0000]" aria-hidden="true" data-id="element-1619" data-mp="hqbe7s5fu7lc" />
            </div>
            <h2 id="checkout-auth-title" className="text-2xl font-black uppercase tracking-tight text-white" data-id="element-1620" data-mp="cwqsayh4d42i">
              Log in to continue
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-gray-400" data-id="element-1621" data-mp="3z6k15kua7ie">
              {planName ? `Your ${planName} selection (${planPrice}) is saved. Log in or create an account and checkout picks up right where you left off.` : 'Log in or create an account to continue to checkout.'}
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-1622" data-mp="mo3jdnv3fq7h">
              <a href={`/login?plan=${plan}`} className="flex-1 rounded-xl bg-[#FF0000] px-5 py-4 text-center text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1623" data-mp="mntrfuqhn8z5">
                Login
              </a>
              <a href={`/signup?plan=${plan}`} className="flex-1 rounded-xl border border-gray-700 px-5 py-4 text-center text-sm font-black uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1624" data-mp="13yfhucmjix94">
                Create account
              </a>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}